// apps/client/src/lib/mfaErrors.ts
//
// Classifies what the Firebase Auth SDK throws during a native TOTP enrolment
// or sign-in challenge into the handful of outcomes the MFA cards can act on,
// and owns the user-facing copy for each.
//
// Pure: no SDK import, so the card suites exercise it unmocked.
//
// Why this exists (findings M1-M3, 2026-08-17): both cards used to funnel every
// failure that was not an App Check throttle into "That code was not right",
// which told an offline user, a rate-limited user, an already-enrolled user and
// a user whose account this client cannot complete that their authenticator was
// wrong, and invited retries that could not succeed (and, under a lockout,
// extended it). Codes below are the client-side names the SDK maps its server
// errors onto:
//   INVALID_CODE                   -> auth/invalid-verification-code
//   SESSION_EXPIRED                -> auth/code-expired
//   INVALID_SESSION_INFO           -> auth/invalid-verification-id
//   MISSING_SESSION_INFO           -> auth/missing-verification-id
//   TOO_MANY_ATTEMPTS_TRY_LATER    -> auth/too-many-requests
//   INVALID_MFA_PENDING_CREDENTIAL -> auth/invalid-multi-factor-session
//   MFA_ENROLLMENT_NOT_FOUND       -> auth/multi-factor-info-not-found
//   SECOND_FACTOR_LIMIT_EXCEEDED   -> auth/maximum-second-factor-count-exceeded
//   SECOND_FACTOR_EXISTS           -> auth/second-factor-already-in-use
//   CREDENTIAL_TOO_OLD_LOGIN_AGAIN -> auth/requires-recent-login
//   TOKEN_EXPIRED / USER_NOT_FOUND -> auth/user-token-expired
//
// Evidence for the wrong-code / session-failure split (Codex CX-M2-1,
// 2026-08-18): the SDK's SERVER_ERROR_MAP has no TOTP-specific entries, so it
// cannot be read as a statement about TOTP outcomes. The wrong-TOTP code is
// confirmed as auth/invalid-verification-code by the SDK's own live-backend
// integration test, firebase-js-sdk
// packages/auth/test/integration/flows/totp.test.ts ("should not enroll if
// incorrect totp supplied" and "should not allow sign-in with incorrect totp").
// SESSION_EXPIRED / INVALID_SESSION_INFO / MISSING_SESSION_INFO describe the
// verification SESSION rather than the digits, so they are session failures: at
// enrol time the secret is dead and only regenerating it can help.

/**
 * Attached by `resolveTotpSignIn` when the resolver carries no TOTP hint. Not a
 * Firebase code (the `valutten/` prefix says so): the SDK never throws it, the
 * client does, and the challenge card treats it as a dead end that only
 * recovery can resolve.
 */
export const MFA_UNSUPPORTED_FACTOR_CODE = 'valutten/mfa-unsupported-factor';

export type MfaFailureKind =
  /** The code was wrong or has rolled over: retry with the current one. */
  | 'wrong_code'
  /** The request never reached Firebase: retry when back online. */
  | 'network'
  /** Firebase has locked the account temporarily: retrying extends it. */
  | 'rate_limited'
  /** The multi-factor session / pending credential is stale: start again. */
  | 'session_expired'
  /** Enrolment needs a fresh first factor first. */
  | 'requires_recent_login'
  /** The account carries a second factor this client cannot complete. */
  | 'unsupported_factor'
  /** Enrolment only: the account already holds its one allowed factor. */
  | 'already_enrolled'
  /**
   * Terminal: GCIP refuses to start TOTP enrolment on an unverified email, and
   * this product has NO email-verification surface at all (no
   * `sendEmailVerification`, no `applyActionCode`; `AuthActionHandler` handles
   * password reset only). So there is nothing the user can do here and no retry
   * that can succeed - only Valutten can flip the flag on the account.
   */
  | 'email_unverified'
  /** The account or the provider is disabled: nothing the user does helps. */
  | 'blocked'
  /** Anything else: say so, do not blame the code. */
  | 'unknown';

export interface MfaFailure {
  kind: MfaFailureKind;
  /** The error's `code` when it carried a string one, else null. */
  code: string | null;
  /** The error's `message` when it carried a string one, else ''. */
  message: string;
}

/** Where the failure happened; the copy for some kinds depends on it. */
export type MfaSurface = 'challenge' | 'enrol';

// Only the digits themselves. `auth/missing-verification-code` stays here: it
// means no digits were sent, which is still a code problem.
const WRONG_CODE_CODES: ReadonlySet<string> = new Set([
  'auth/invalid-verification-code',
  'auth/missing-verification-code',
]);

const NETWORK_CODES: ReadonlySet<string> = new Set([
  'auth/network-request-failed',
  'auth/timeout',
]);

const RATE_LIMITED_CODES: ReadonlySet<string> = new Set(['auth/too-many-requests']);

const SESSION_EXPIRED_CODES: ReadonlySet<string> = new Set([
  'auth/invalid-multi-factor-session',
  'auth/missing-multi-factor-session',
  'auth/multi-factor-info-not-found',
  'auth/missing-multi-factor-info',
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/null-user',
  // The verification session, not the digits (see the doc block above).
  'auth/code-expired',
  'auth/invalid-verification-id',
  'auth/missing-verification-id',
]);

const REQUIRES_RECENT_LOGIN_CODES: ReadonlySet<string> = new Set(['auth/requires-recent-login']);

const ALREADY_ENROLLED_CODES: ReadonlySet<string> = new Set([
  'auth/maximum-second-factor-count-exceeded',
  'auth/second-factor-already-in-use',
]);

/** Terminal for this account: no code, and no retry, can move past these. */
const BLOCKED_CODES: ReadonlySet<string> = new Set([
  'auth/user-disabled',
  'auth/operation-not-allowed',
]);

/**
 * Terminal, and distinct from `blocked` because the remedy is different and
 * nameable: the account's email is not verified, so GCIP refuses to begin TOTP
 * enrolment. Previously classified `unknown`, which showed "We could not verify
 * that code right now. Try again in a moment" and invited a retry loop that
 * could never succeed.
 */
const EMAIL_UNVERIFIED_CODES: ReadonlySet<string> = new Set(['auth/unverified-email']);

/** Read a string `code` off anything the SDK (or we) may have thrown. */
export function readErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** Read a string `message` off anything the SDK (or we) may have thrown. */
export function readErrorMessage(err: unknown): string {
  if (typeof err !== 'object' || err === null) return '';
  const message = (err as { message?: unknown }).message;
  return typeof message === 'string' ? message : '';
}

/**
 * Sort a thrown value into the outcome the UI should present. App Check /
 * reCAPTCHA throttles are NOT handled here: callers run `classifyAuthError`
 * first and hand those to AppCheckRecoveryBanner, as before.
 */
export function classifyMfaFailure(err: unknown): MfaFailure {
  const code = readErrorCode(err);
  const message = readErrorMessage(err);
  const as = (kind: MfaFailureKind): MfaFailure => ({ kind, code, message });
  if (code === null) return as('unknown');
  if (code === MFA_UNSUPPORTED_FACTOR_CODE) return as('unsupported_factor');
  if (WRONG_CODE_CODES.has(code)) return as('wrong_code');
  if (NETWORK_CODES.has(code)) return as('network');
  if (RATE_LIMITED_CODES.has(code)) return as('rate_limited');
  if (SESSION_EXPIRED_CODES.has(code)) return as('session_expired');
  if (REQUIRES_RECENT_LOGIN_CODES.has(code)) return as('requires_recent_login');
  if (ALREADY_ENROLLED_CODES.has(code)) return as('already_enrolled');
  if (EMAIL_UNVERIFIED_CODES.has(code)) return as('email_unverified');
  if (BLOCKED_CODES.has(code)) return as('blocked');
  return as('unknown');
}

// ── Copy ─────────────────────────────────────────────────────────────────────

export const WRONG_CODE_MESSAGE =
  'That code was not right, or it expired. Codes change every 30 seconds, try the current one.';

export const NETWORK_MESSAGE =
  'We could not reach the sign-in service. Check your connection and try again.';

export const RATE_LIMITED_MESSAGE =
  'Too many attempts. For your security, wait a few minutes before trying again.';

export const CHALLENGE_SESSION_EXPIRED_MESSAGE =
  'Your sign-in session has expired. Go back to sign in and try again.';

export const ENROL_SESSION_EXPIRED_MESSAGE =
  'Your setup session has expired. Start again to get a fresh code.';

export const REQUIRES_RECENT_LOGIN_MESSAGE =
  'For your security, please sign in again before setting up two-factor authentication.';

/** Fallback only: `resolveTotpSignIn` throws with its own, surface-specific wording. */
export const UNSUPPORTED_FACTOR_MESSAGE =
  'No authenticator is set up for this account. Request a reset from Valutten using the link below.';

export const ALREADY_ENROLLED_MESSAGE =
  'An authenticator is already set up for this account, so the code you just scanned will not be used. Continue with the authenticator you set up earlier.';

/**
 * The same kind at GENERATE time. Nothing was scanned in this session, so the
 * verify-time wording ("the code you just scanned will not be used") would
 * describe something that never happened. This is the shape an already-enrolled
 * user hits when they resume an invitation link: GCIP refuses to issue a second
 * secret, and the correct outcome is Continue, not a retry that cannot succeed.
 */
export const GENERATE_ALREADY_ENROLLED_MESSAGE =
  'An authenticator is already set up for this account. Continue with it.';

/**
 * The same kind on the challenge surface: nothing was scanned here, so the only
 * useful instruction is to read the current code off the enrolled app.
 */
export const CHALLENGE_ALREADY_ENROLLED_MESSAGE =
  'An authenticator is already set up for this account. Enter the current code from it.';

/**
 * `auth/user-disabled` / `auth/operation-not-allowed`: the account or the
 * provider is off. Promise nothing, and do not invite a retry loop.
 */
export const BLOCKED_MESSAGE = 'Sign-in for this account is not available at the moment.';

/**
 * `auth/unverified-email` at enrolment. Names the blocker and the only party who
 * can clear it. There is deliberately no "try again" here: nothing the user does
 * in this app changes `emailVerified`.
 */
export const EMAIL_UNVERIFIED_MESSAGE =
  'Your email address must be verified before two-factor authentication can be set up. Contact Valutten to have it verified.';

export const CHALLENGE_UNKNOWN_MESSAGE =
  'We could not verify that code right now. Try again in a moment.';

export const ENROL_UNKNOWN_MESSAGE =
  'We could not verify that code right now. Try again in a moment, or start again for a fresh code.';

/**
 * The message shown for a classified failure. Unknown failures append the
 * SDK code (when there is one) so support can act on a screenshot without
 * pretending we know what went wrong.
 */
export function mfaFailureMessage(failure: MfaFailure, surface: MfaSurface): string {
  switch (failure.kind) {
    case 'wrong_code':
      return WRONG_CODE_MESSAGE;
    case 'network':
      return NETWORK_MESSAGE;
    case 'rate_limited':
      return RATE_LIMITED_MESSAGE;
    case 'session_expired':
      return surface === 'challenge' ? CHALLENGE_SESSION_EXPIRED_MESSAGE : ENROL_SESSION_EXPIRED_MESSAGE;
    case 'requires_recent_login':
      return surface === 'challenge' ? CHALLENGE_SESSION_EXPIRED_MESSAGE : REQUIRES_RECENT_LOGIN_MESSAGE;
    case 'unsupported_factor':
      // The lib's own wording is authoritative (it differs per app); the
      // constant is a fallback for a code that arrived without a message.
      return failure.message || UNSUPPORTED_FACTOR_MESSAGE;
    case 'already_enrolled':
      return surface === 'challenge' ? CHALLENGE_ALREADY_ENROLLED_MESSAGE : ALREADY_ENROLLED_MESSAGE;
    case 'email_unverified':
      // Same copy on both surfaces: the blocker is the account, not the moment.
      return EMAIL_UNVERIFIED_MESSAGE;
    case 'blocked':
      return failure.code ? `${BLOCKED_MESSAGE} (${failure.code})` : BLOCKED_MESSAGE;
    case 'unknown': {
      const base = surface === 'challenge' ? CHALLENGE_UNKNOWN_MESSAGE : ENROL_UNKNOWN_MESSAGE;
      return failure.code ? `${base} (${failure.code})` : base;
    }
    default: {
      const exhaustive: never = failure.kind;
      return exhaustive;
    }
  }
}

// ── Sign-in refusal from the MFA blocking function ──────────────────────────
//
// `beforeSignInMfaGate` (services/admin-functions) rejects an unenrolled
// in-scope sign-in by throwing `HttpsError('permission-denied', MSG_MFA_REQUIRED)`
// from a blocking function. The web SDK surfaces that to the client as
// `auth/internal-error` with the server's message embedded in `error.message`,
// so LoginPage's `default:` arm used to print the raw platform string.
//
// The client cannot IMPORT the constant: it lives in a different deployment
// codebase (admin-functions), and apps/client does not compile against it. So
// the literal is pinned here and `mfaErrors.wireConstants.test.ts` reads
// `beforeSignInMfaGate.ts` off disk and asserts byte equality. If the server
// wording changes, that test goes red rather than the branch silently ceasing
// to match at runtime.

/**
 * MUST equal `MSG_MFA_REQUIRED` in
 * `services/admin-functions/src/mfa/beforeSignInMfaGate.ts`.
 * Pinned by test, never imported. Do not reword one side alone.
 */
export const MFA_REQUIRED_SIGNIN_MESSAGE =
  'Multi-factor authentication is required for this account. Enrol a second factor to sign in, or contact your administrator.';

/**
 * What the user is actually shown (G6, from V-08).
 *
 * The previous wording, "Ask your administrator to arrange enrolment", named a
 * remedy that does not exist at that role. In the client app the only way past a
 * refused unenrolled account is a founder-granted
 * `admin_config/mfaRecovery__{uid}` exemption; a tenant `administrator` holds no
 * capability that can arrange enrolment. On the admin portal it was worse:
 * that app is founder-only, so it told the sole founder to ask their
 * administrator.
 *
 * The copy is app-specific for the same reason UNSUPPORTED_FACTOR_MESSAGE and
 * EMAIL_UNVERIFIED_MESSAGE already are: the remedy differs, and there is no
 * shared package to hold one string.
 */
export const MFA_REQUIRED_SIGNIN_DISPLAY_MESSAGE =
  'Two-factor setup is required before you can sign in. Contact Valutten to arrange your enrolment window.';

/**
 * True when a failed sign-in is the blocking function's MFA refusal.
 *
 * Both conditions are required. `auth/internal-error` alone is a generic
 * server-side failure and must NOT be relabelled as an MFA problem; the message
 * match is what makes the branch specific.
 */
export function isMfaEnrolmentRequiredSignInError(err: unknown): boolean {
  return (
    readErrorCode(err) === 'auth/internal-error' &&
    readErrorMessage(err).includes(MFA_REQUIRED_SIGNIN_MESSAGE)
  );
}

/**
 * The message shown on the sign-in form for that refusal.
 *
 * Deliberately NOT the server string plus a suffix. `MSG_MFA_REQUIRED` is a wire
 * constant used to RECOGNISE the refusal (it has to match the server byte for
 * byte, which is what its test pins); it is written for a log, and appending an
 * app-specific next step to it produced a two-sentence message that said the
 * same thing twice. What the user needs is the actionable half.
 */
export const MFA_REQUIRED_SIGNIN_FULL_MESSAGE = MFA_REQUIRED_SIGNIN_DISPLAY_MESSAGE;
